import { z } from 'zod';
import {
  attributionSchema,
  currencySchema,
  i18nTextSchema,
  localeSchema,
  moneySchema,
  provinceCodeSchema,
} from './common.js';

/**
 * Order contracts. Two rules from CLAUDE.md §3 dominate this file:
 *
 *  1. Line items are SNAPSHOTS. The full product configuration and price are
 *     frozen at order time. Nothing here is a live reference — if a merchant
 *     renames a product or changes its price tomorrow, yesterday's order and
 *     its receipt must read exactly as they did when the customer paid.
 *  2. Totals come from one function, `calculateOrderTotals()` in `order-core`,
 *     and the snapshot carries `subtotal + adjustments[] + shipping = total`
 *     so the v2 promotion engine slots in without a schema migration.
 */

export const orderStatusSchema = z.enum([
  'draft',
  'pending_confirm',
  'awaiting_payment',
  'paid',
  'fulfilling',
  'confirmed',
  'shipped',
  'collected',
  'remitted',
  // §4 services branch: a booking is an order, so it shares this machine.
  'scheduled',
  'no_show',
  'completed',
  'cancelled',
  'refunded',
]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const paymentTypeSchema = z.enum(['prepaid', 'cod']);
export type PaymentType = z.infer<typeof paymentTypeSchema>;

export const orderChannelSchema = z.enum(['telegram', 'web', 'bot', 'admin']);

/** Frozen copy of everything the customer actually bought. */
export const lineItemSnapshotSchema = z.object({
  productName: i18nTextSchema,
  /** e.g. { size: "M", color: "Black" } — whatever the vertical defines. */
  options: z.record(z.string().max(30), z.string().max(60)).default({}),
  sku: z.string().nullable().default(null),
  imageUrl: z.string().url().nullable().default(null),
  /** Kept for analytics only; never resolved back for display. */
  productSlug: z.string().nullable().default(null),
});
export type LineItemSnapshot = z.infer<typeof lineItemSnapshotSchema>;

export const orderItemSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid().nullable(),
  variantId: z.string().uuid().nullable(),
  snapshot: lineItemSnapshotSchema,
  unitPrice: moneySchema,
  quantity: z.number().int().min(1),
  lineTotal: moneySchema,
});
export type OrderItemDto = z.infer<typeof orderItemSchema>;

/**
 * Adjustment slot. Empty in v1 by design (§3 `promotion`): the field exists so
 * discounts, vouchers and free-shipping land as data, not as a schema change.
 */
export const adjustmentSchema = z.object({
  kind: z.enum(['discount', 'fee', 'shipping_discount', 'rounding']),
  label: i18nTextSchema,
  amount: z.number().int(), // signed minor units
  meta: z.record(z.unknown()).default({}),
});
export type Adjustment = z.infer<typeof adjustmentSchema>;

/** Delivery address, snapshotted onto the order like the line items. */
export const deliverySnapshotSchema = z.object({
  receiverName: z.string().min(1).max(80),
  phone: z.string().min(6).max(24),
  provinceCode: provinceCodeSchema,
  district: z.string().max(80).nullable().default(null),
  addressLine: z.string().max(300),
  note: z.string().max(300).nullable().default(null),
});
export type DeliverySnapshot = z.infer<typeof deliverySnapshotSchema>;

export const orderTotalsSchema = z.object({
  currency: currencySchema,
  subtotal: z.number().int(),
  adjustments: z.array(adjustmentSchema),
  shipping: z.number().int(),
  total: z.number().int(),
});
export type OrderTotals = z.infer<typeof orderTotalsSchema>;

export const orderSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  shopId: z.string().uuid(),
  status: orderStatusSchema,
  paymentType: paymentTypeSchema,
  currency: currencySchema,
  items: z.array(orderItemSchema),
  subtotal: moneySchema,
  adjustments: z.array(adjustmentSchema),
  shipping: moneySchema,
  total: moneySchema,
  /**
   * What the customer is actually asked to transfer — `total` plus a small
   * per-order offset so an incoming ABA notification maps to exactly one
   * order (§3 `payment`). Equal to `total` for COD.
   */
  chargeAmount: moneySchema,
  delivery: deliverySnapshotSchema.nullable(),
  customer: z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    phone: z.string().nullable(),
    telegramUserId: z.number().nullable(),
  }),
  note: z.string().nullable(),
  channel: orderChannelSchema,
  locale: localeSchema,
  attribution: attributionSchema.nullable(),
  exchangeOfOrderId: z.string().uuid().nullable(),
  placedAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  cancelledAt: z.string().datetime().nullable(),
  cancelReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrderDto = z.infer<typeof orderSchema>;

export const orderSummarySchema = orderSchema.pick({
  id: true,
  code: true,
  status: true,
  paymentType: true,
  total: true,
  currency: true,
  createdAt: true,
  placedAt: true,
}).extend({
  customerName: z.string().nullable(),
  itemCount: z.number().int(),
});
export type OrderSummaryDto = z.infer<typeof orderSummarySchema>;

/* ------------------------------------------------------------------ */
/* Checkout                                                            */
/* ------------------------------------------------------------------ */

export const cartLineSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid().nullable(),
  quantity: z.number().int().min(1).max(99),
  /**
   * F&B modifier picks (§4), as group id → option ids. Empty for retail and
   * services, and re-validated server-side against the groups the item
   * actually has — the client's copy of the menu may be minutes old.
   */
  modifiers: z.record(z.string().uuid(), z.array(z.string().uuid()).max(20)).default({}),
});
export type CartLine = z.infer<typeof cartLineSchema>;

/**
 * How an order reaches the customer, per vertical (§4).
 *
 * Retail ships to a province from the merchant's own price table. F&B is
 * pickup or a short local hop with a flat fee — §4 rules out provincial
 * shipping there. Services deliver nothing at all: the customer comes to the
 * shop at a time they picked.
 */
export const fulfilmentSchema = z.object({
  method: z.enum(['ship', 'pickup', 'local_delivery', 'dine_in', 'appointment']).default('ship'),
  /** F&B: the pickup or delivery time the customer chose. */
  slotStartsAt: z.string().datetime().nullable().default(null),
});
export type FulfilmentInput = z.infer<typeof fulfilmentSchema>;

/** Services: the slot and the person, chosen before checkout (§4). */
export const checkoutBookingSchema = z.object({
  resourceId: z.string().uuid(),
  startsAt: z.string().datetime(),
  note: z.string().max(500).optional(),
});

export const checkoutRequestSchema = z.object({
  lines: z.array(cartLineSchema).min(1).max(50),
  paymentType: paymentTypeSchema,
  /**
   * Optional from v3: an appointment has nowhere to deliver to, and an F&B
   * pickup order has no address. Retail still requires it, enforced in the
   * checkout service where the vertical is known.
   */
  delivery: deliverySnapshotSchema.optional(),
  fulfilment: fulfilmentSchema.optional(),
  booking: checkoutBookingSchema.optional(),
  customer: z.object({
    name: z.string().min(1).max(80),
    phone: z.string().min(6).max(24),
  }),
  note: z.string().max(500).optional(),
  locale: localeSchema.default('km'),
  channel: orderChannelSchema.default('web'),
  attribution: attributionSchema.partial().optional(),
  /** Voucher codes the customer applied; re-validated server-side. */
  promotionCodes: z.array(z.string().max(32)).max(5).default([]),
  /**
   * Idempotency key from the client. A customer on a bad 4G connection taps
   * "Place order" three times; they get one order.
   */
  idempotencyKey: z.string().min(8).max(64),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/** Server-side quote so the storefront can show totals before committing. */
export const quoteRequestSchema = z.object({
  lines: z.array(cartLineSchema).min(1).max(50),
  provinceCode: provinceCodeSchema.optional(),
  /** Same shape as checkout, so the quote prices what will be charged. */
  fulfilment: fulfilmentSchema.optional(),
  paymentType: paymentTypeSchema.default('prepaid'),
  /** Voucher codes typed at checkout or carried by a `?start=promo_X` link. */
  promotionCodes: z.array(z.string().max(32)).max(5).default([]),
});

export const quoteResponseSchema = z.object({
  currency: currencySchema,
  subtotal: moneySchema,
  adjustments: z.array(adjustmentSchema),
  shipping: moneySchema,
  total: moneySchema,
  /** Reference conversion for the other currency (§6.4). */
  totalConverted: moneySchema.nullable(),
  unavailableLines: z.array(
    z.object({
      productId: z.string().uuid(),
      variantId: z.string().uuid().nullable(),
      reason: z.enum(['product_unavailable', 'variant_unavailable', 'out_of_stock']),
      availableStock: z.number().int().nullable(),
    }),
  ),
  /** Promotions that actually applied, for the cart summary. */
  appliedPromotions: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: i18nTextSchema,
        code: z.string().nullable(),
        amount: z.number().int(),
        freeShipping: z.boolean(),
      }),
    )
    .default([]),
  /** Codes the customer typed that matched nothing — told, not ignored. */
  invalidCodes: z.array(z.string()).default([]),
});
export type QuoteResponse = z.infer<typeof quoteResponseSchema>;

export const checkoutResponseSchema = z.object({
  order: orderSchema,
  /** Present for prepaid orders: the QR the customer scans right now. */
  payment: z
    .object({
      id: z.string().uuid(),
      provider: z.enum(['manual_qr', 'bakong_api', 'aba_payway', 'cod']),
      qrPayload: z.string().nullable(),
      deepLink: z.string().nullable(),
      amount: moneySchema,
      expiresAt: z.string().datetime().nullable(),
      payeeLabel: z.string().nullable(),
    })
    .nullable(),
  /** Deep link that converts this buyer into a bot subscriber (§6.3). */
  botDeepLink: z.string().nullable(),
});

/* ------------------------------------------------------------------ */
/* Merchant actions                                                    */
/* ------------------------------------------------------------------ */

export const orderActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('confirm') }),
  z.object({ action: z.literal('mark_paid'), note: z.string().max(200).optional() }),
  z.object({ action: z.literal('start_fulfilling') }),
  z.object({
    action: z.literal('ship'),
    carrier: z.string().max(60).optional(),
    trackingCode: z.string().max(60).optional(),
  }),
  z.object({ action: z.literal('mark_collected') }),
  z.object({ action: z.literal('mark_remitted'), batchRef: z.string().max(60).optional() }),
  z.object({ action: z.literal('complete') }),
  z.object({ action: z.literal('cancel'), reason: z.string().max(200) }),
  z.object({
    action: z.literal('refund'),
    amount: z.number().int().min(1),
    reason: z.string().max(200),
    reference: z.string().max(80).optional(),
  }),
]);
export type OrderAction = z.infer<typeof orderActionSchema>;

/** Fashion size-exchange = a NEW order linked to the original (§3 `order`). */
export const createExchangeSchema = z.object({
  originalOrderId: z.string().uuid(),
  lines: z.array(cartLineSchema).min(1).max(50),
  reason: z.string().max(200),
});

export const orderListQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  paymentType: paymentTypeSchema.optional(),
  q: z.string().max(60).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
});
